import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Search, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import Navigation from "./Navigation";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiService } from "../services/apiService";
import { transformClubs } from "../utils/dataTransformers";
import { filterClubsBySearch, filterClubsByType } from "../utils/locationUtils";
import { toast } from "sonner";
import ClubCard from "./ui/ClubCard";
import { VALID_CLUB_TYPES } from "../constants/clubConstants";

/** Page size for GET /clubs (repeat with skip until API returns fewer than this). */
const RUN_CLUBS_PAGE_FETCH_SIZE = 100;

const isFullClubsPage = (batch) => batch.length >= RUN_CLUBS_PAGE_FETCH_SIZE;

const RunClubsPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [rawClubs, setRawClubs] = useState([]);
  const [clubsLoading, setClubsLoading] = useState(true);
  const [loadMoreClubsLoading, setLoadMoreClubsLoading] = useState(false);
  const [hasMoreClubsFromApi, setHasMoreClubsFromApi] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const rawClubsRef = useRef([]);
  rawClubsRef.current = rawClubs;

  // URL-derived city — when hero searches /runclubs?city=Miami, we pass it
  // as user_city so clubs in that city sort to the top of the list. The
  // backend doesn't support a hard `city` filter on this endpoint, so the
  // client-side filterClubsBySearch below provides the actual filtering.
  // We deliberately pass NO user_lat/user_lon to avoid a default distance-
  // based ordering when an explicit city was searched.
  const cityFromUrl = (searchParams.get('city') || '').trim();

  const fetchClubsPage = useCallback(async (skip) => {
    const batch = await apiService.clubs.getAll({
      limit: RUN_CLUBS_PAGE_FETCH_SIZE,
      skip,
      ...(cityFromUrl ? { user_city: cityFromUrl } : {}),
    });
    return Array.isArray(batch) ? batch : [];
  }, [cityFromUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setClubsLoading(true);
      try {
        const batch = await fetchClubsPage(0);
        if (!cancelled) {
          setRawClubs(batch);
          setHasMoreClubsFromApi(isFullClubsPage(batch));
        }
      } catch (e) {
        if (!cancelled) {
          toast.error("Could not load clubs. Please try again.");
          setRawClubs([]);
          setHasMoreClubsFromApi(false);
        }
      } finally {
        if (!cancelled) setClubsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchClubsPage]);

  const refetchClubs = useCallback(async () => {
    setClubsLoading(true);
    try {
      const batch = await fetchClubsPage(0);
      setRawClubs(batch);
      setHasMoreClubsFromApi(isFullClubsPage(batch));
      setCurrentPage(1);
    } catch (e) {
      toast.error("Could not refresh clubs.");
    } finally {
      setClubsLoading(false);
    }
  }, [fetchClubsPage]);

  const handleLoadMoreClubsFromApi = useCallback(async () => {
    if (loadMoreClubsLoading || !hasMoreClubsFromApi) return;
    setLoadMoreClubsLoading(true);
    try {
      const prev = rawClubsRef.current;
      const batch = await fetchClubsPage(prev.length);
      const seen = new Set(prev.map((c) => c?.id).filter(Boolean));
      const merged = [...prev];
      for (const c of batch) {
        if (c?.id && !seen.has(c.id)) {
          seen.add(c.id);
          merged.push(c);
        }
      }
      setRawClubs(merged);
      setHasMoreClubsFromApi(isFullClubsPage(batch));
    } catch (e) {
      toast.error("Could not load more clubs.");
    } finally {
      setLoadMoreClubsLoading(false);
    }
  }, [fetchClubsPage, loadMoreClubsLoading, hasMoreClubsFromApi]);

  // Transform data using our utility
  const clubs = useMemo(() => {
    if (!rawClubs?.length) return [];
    return transformClubs(rawClubs);
  }, [rawClubs]);

  // Filter clubs based on search and type
  const filteredClubs = useMemo(() => {
    let filtered = filterClubsBySearch(clubs, searchQuery);
    filtered = filterClubsByType(filtered, selectedType);
    return filtered;
  }, [clubs, searchQuery, selectedType]);

  const clubsPerPage = 9;
  
  // Autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Filter options: "All" + valid club types from constants
  const clubTypes = ["All", ...VALID_CLUB_TYPES];

  // Get unique cities from clubs data
  const getUniqueCities = () => {
    const cities = clubs.map(club => `${club.city}${club.state ? `, ${club.state}` : ''}`).filter(Boolean);
    return [...new Set(cities)].sort();
  };

  // Generate suggestions based on search query
  const generateSuggestions = (query) => {
    if (!query || query.length < 2) return [];
    
    const cities = getUniqueCities();
    return cities.filter(city => 
      city.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 5); // Limit to 5 suggestions
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.length >= 2) {
      const newSuggestions = generateSuggestions(value);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (city) => {
    setSearchQuery(city);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (searchQuery.length >= 2) {
      const newSuggestions = generateSuggestions(searchQuery);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    }
  };

  // Handle input blur (with delay to allow clicking on suggestions)
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };


  useEffect(() => {
    const cityFromUrl = searchParams.get('city');
    const typeFromUrl = searchParams.get('type');
    
    if (cityFromUrl) {
      setSearchQuery(cityFromUrl);
    }
    
    if (typeFromUrl) {
      // Capitalize first letter to match clubTypes format
      const capitalizedType = typeFromUrl.charAt(0).toUpperCase() + typeFromUrl.slice(1);
      if (clubTypes.includes(capitalizedType)) {
        setSelectedType(capitalizedType);
      }
    }
  }, [searchParams]);



  // Pagination calculations
  const totalPages = Math.ceil(filteredClubs.length / clubsPerPage);
  const startIndex = (currentPage - 1) * clubsPerPage;
  const endIndex = startIndex + clubsPerPage;
  const currentClubs = filteredClubs.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of clubs section
    const clubsSection = document.querySelector('.clubs-section');
    if (clubsSection) {
      clubsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };



  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-runcards-background">
        <div className="text-runcards-text-primary">Loading...</div>
      </div>
    );
  }

  // Show loading state while fetching clubs
  if (clubsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-runcards-background">
        <div className="text-runcards-text-primary">Loading clubs...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-runcards-background text-runcards-text-primary pwa-content">
      <style>{`
        button:focus,
        a:focus,
        input:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        button:focus-visible,
        a:focus-visible,
        input:focus-visible {
          outline: none !important;
          box-shadow: none !important;
        }
      `}</style>
      <Navigation />
      
      {/* Hero Section with Background Image */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/Marcus_Speed_and_Form.png')",
            filter: "grayscale(100%) brightness(0.6)"
          }}
        />
        <div className="absolute inset-0 bg-runcards-background/30" />
        
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-6xl mx-auto pt-80 sm:pt-96 lg:pt-112">
          {/* Main Title */}
          <div className="mb-6">
            <h2 className="text-runcards-title sm:text-4xl md:text-5xl lg:text-6xl font-runcards-bold mb-4 text-runcards-text-primary w-full" style={{letterSpacing: '-0.02em', transform: 'scaleX(1.5)', whiteSpace: 'nowrap'}}>
              FIND YOUR CLUB
            </h2>
            <p className="text-runcards-xl sm:text-runcards-xxl font-runcards text-runcards-text-tertiary">
              Discover local running communities.
            </p>
          </div>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <div className="flex">
                <div className="relative flex-1">
                  <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-runcards-text-secondary w-5 h-5 z-10" />
                  <Input
                    type="text"
                    variant="runcards"
                    placeholder="Search by club name or city..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    className="pl-12 pr-4 h-14 text-lg rounded-l-runcards-lg rounded-r-none border-r-0 font-runcards"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 bg-runcards-foreground backdrop-blur-xl border border-runcards-border rounded-b-runcards-lg mt-1 z-50">
                      {suggestions.map((city, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionSelect(city)}
                          className="w-full text-left px-4 py-3 text-runcards-text-primary hover:bg-runcards-surface transition-colors font-runcards"
                        >
                          {city}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  variant="runcards-secondary"
                  size="runcards-large"
                  onClick={() => {}}
                  className="rounded-r-runcards-lg rounded-l-none h-14 px-6 border-l-0"
                >
                  <Search className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {clubTypes.map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-4 py-2 rounded-runcards-round text-runcards-sm font-runcards transition-all duration-200 ${
                  selectedType === type
                    ? 'bg-runcards-primary text-runcards-text-on-light'
                    : 'bg-runcards-surface text-runcards-text-primary hover:bg-runcards-foreground border border-runcards-border'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Featured Clubs Section */}
      <section className="py-16 px-4 sm:px-6 bg-runcards-background clubs-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-12 gap-4">
            <div className="text-center sm:text-left">
              <h2 className="text-runcards-xxl sm:text-runcards-title md:text-3xl lg:text-4xl font-runcards-bold text-runcards-text-primary mb-4">
                {searchQuery.trim() ? `Clubs in ${searchQuery}` : 'Featured Clubs'}
              </h2>
              <p className="text-runcards-text-secondary text-runcards-lg font-runcards">
                <span className="text-white" style={{ fontSize: '1.2em' }}>{filteredClubs?.length || 0} clubs</span>
                <span> found</span>
                {searchQuery.trim() && ` in ${searchQuery}`}
              </p>
            </div>
          </div>

          {currentClubs && currentClubs.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentClubs.map((club) => (
                <ClubCard
                  key={club.id}
                  club={club}
                  buttonText="View Club"
                  onConnectionChange={(clubId, isConnected) => {
                    // Refresh clubs data when connect/disconnect happens
                    refetchClubs();
                  }}
                />
              ))}
            </div>
          ) : null}

          {hasMoreClubsFromApi && (
            <div className="flex justify-center mt-10">
              <Button
                type="button"
                variant="runcards-secondary"
                size="runcards-medium"
                disabled={loadMoreClubsLoading || clubsLoading}
                onClick={handleLoadMoreClubsFromApi}
                className="font-runcards"
              >
                {loadMoreClubsLoading ? "Loading…" : "Load more clubs"}
              </Button>
            </div>
          )}

          {!currentClubs?.length ? (
            <div className="text-center py-16">
              <div className="bg-runcards-foreground/80 backdrop-blur-xl border border-runcards-border rounded-runcards-lg p-8 max-w-md mx-auto">
                <MapPin className="w-16 h-16 text-runcards-text-secondary-400 mx-auto mb-4" />
                <h3 className="text-xl font-runcards-bold text-white mb-2">No clubs found</h3>
                <p className="text-runcards-text-secondary-400 mb-6">
                  We don't have any running clubs in this area yet. Try searching for nearby cities or check out clubs in other locations.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    variant="runcards-secondary"
                    size="runcards-medium"
                    onClick={() => navigate('/runclubs')}
                  >
                    Browse All Clubs
                  </Button>
                  <Button 
                    variant="runcards-outline"
                    size="runcards-medium"
                    onClick={() => {
                      const heroSection = document.querySelector('section.relative.min-h-screen');
                      if (heroSection) {
                        heroSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }}
                  >
                    Search Different City
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-12 space-x-4">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="text-white hover:text-runcards-text-secondary-300 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-runcards"
              >
                Previous
              </button>
              
              <div className="flex space-x-4">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`text-lg font-runcards ${
                      currentPage === page
                        ? 'text-white'
                        : 'text-runcards-text-secondary-400 hover:text-white'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="text-white hover:text-runcards-text-secondary-300 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-runcards"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default RunClubsPage;
